import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { has, set } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';

import { ONE_TRUST_CONFIG, OneTrustConfig } from './one-trust.interface';

export enum LoadingStatus {
  NotLoaded,
  Loading,
  Loaded,
}

const ONE_TRUST_STUB_SRC =
  'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';

type OneTrustWindow = Window & {
  OneTrust?: unknown;
  OptanonWrapper?: () => void;
};

@Injectable()
export class OneTrustLoaderService {
  private status$ = new BehaviorSubject<LoadingStatus>(LoadingStatus.NotLoaded);

  get status() {
    return this.status$.value;
  }

  private window: OneTrustWindow;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(ONE_TRUST_CONFIG) private config: OneTrustConfig,
  ) {
    this.window = document.defaultView || ({} as OneTrustWindow);
  }

  loadAsStream(): Observable<LoadingStatus> {
    this.load();
    return this.status$.asObservable();
  }

  isLoaded(): boolean {
    return this.status === LoadingStatus.Loaded;
  }

  load(): void {
    if (!this.config) {
      throw new Error('No OneTrust config provided');
    }

    if (
      this.config.disabled ||
      this.status === LoadingStatus.Loading ||
      this.status === LoadingStatus.Loaded
    ) {
      return;
    }

    if (has(this.window, 'OneTrust')) {
      this.status$.next(LoadingStatus.Loaded);
      return;
    }

    if (!this.config.domainScript?.trim()) {
      throw new Error(
        'OneTrust domainScript config is required to load OneTrust',
      );
    }

    const existingWrapper = this.window.OptanonWrapper;
    set(this.window, 'OptanonWrapper', () => {
      existingWrapper?.();
      this.status$.next(LoadingStatus.Loaded);
    });

    const existingScript = this.document.querySelector(
      `script[src="${ONE_TRUST_STUB_SRC}"][data-domain-script="${this.config.domainScript}"]`,
    );

    if (existingScript) {
      this.status$.next(LoadingStatus.Loading);
      return;
    }

    this.status$.next(LoadingStatus.Loading);

    const script = this.document.createElement('script');
    script.type = 'text/javascript';
    script.charset = 'UTF-8';
    script.src = ONE_TRUST_STUB_SRC;
    script.setAttribute('data-domain-script', this.config.domainScript);

    this.document.head.appendChild(script);
  }
}
